/**
 * FieldWrapper.tsx
 *
 * Wrapper component for form fields that provides consistent layout
 * for label, help text, field content, and error messages
 */

import React from 'react';
import { Field } from '../../../types/types';
import { FieldLabel } from './FieldLabel';
import { ErrorMessage } from './ErrorMessage';

interface FieldWrapperProps {
  /** Field configuration */
  field: Field;

  /** Field content to render */
  children: React.ReactNode;

  /** Array of error messages */
  errors?: string[];

  /** Whether the field has an error */
  hasError?: boolean;

  /** Additional CSS classes for the wrapper */
  className?: string;

  /** Whether to show the label */
  showLabel?: boolean;

  /** Whether to show help text */
  showHelpText?: boolean;

  /** Whether in preview mode (hides label since it's shown in field header) */
  isPreview?: boolean;
}

export const FieldWrapper: React.FC<FieldWrapperProps> = ({
  field,
  children,
  errors = [],
  hasError = false,
  className = '',
  showLabel = true,
  showHelpText = true,
  isPreview = false,
}) => {
  // In preview mode, label and help text are shown in the field header/footer, so hide them here
  const shouldShowLabel = showLabel && !isPreview;
  const shouldShowHelpText = showHelpText && !isPreview;
  // Information boxes don't need wrapper structure
  if (field.type === 'information-box') {
    return <>{children}</>;
  }

  return (
    <div className={`space-y-1 ${isPreview ? '' : 'mb-4'} ${className}`}>
      {/* Label */}
      {shouldShowLabel && field.label && field.type !== 'checkbox' && field.type !== 'yes-no-toggle' && (
        <FieldLabel
          htmlFor={field.id}
          label={field.label}
          required={field.required}
          alignment={field.layout?.alignment}
        />
      )}

      {/* Description text (shown before field for most types) */}
      {showHelpText && field.description && !['information-box'].includes(field.type) && (
        <span className="text-sm text-muted-foreground whitespace-pre-wrap break-words block">
          {field.description}
        </span>
      )}

      {/* Field content */}
      {children}

      {/* Help text (shown after field) */}
      {shouldShowHelpText && field.helpText && (
        <p className="text-xs text-muted-foreground break-words">
          {field.helpText}
        </p>
      )}

      {/* Error messages */}
      {hasError && errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((error, index) => (
            <ErrorMessage key={index} message={error} />
          ))}
        </div>
      )}
    </div>
  );
};
